import detemplate from '../util/detemplate';
import { HLS } from '../player/interfaces';
import {
  KsdkEventData,
  MessageTemplates,
  PlayerNames
} from '@kollective-frontend/ksdk-api';
import { ShakaPlayer } from '../player/interfaces';
import { isString } from '../common/utils/stringify';
import { noop } from '../common/constants';
import { Logger } from '../log/Logger';
import { Level, LogLevels } from '../log/LoggerConstants';
import { LogMessage } from '../log/Interfaces';
import { dispatchError } from '../events/dispatch';

const { INCORRECT_TYPE, PASSED_INVALID, MISSING_FIELDS } = MessageTemplates;

// Unfortunately there is no way in typescript to do a recursive structure of
// this type so we need to define as many levels as we need.
type TestBlockA = ObjTestFunc | string | Record<string, ObjTestFunc | string>;
type TestBlockB = TestBlockA | Record<string, TestBlockA>;
type TestBlockC = TestBlockB | Record<string, TestBlockB>;
type TestBlockD = TestBlockC | Record<string, TestBlockC>;
type TestBlockE = TestBlockD | Record<string, TestBlockD>;
export type FullObjTestBlock = TestBlockE | Record<string, TestBlockE>;

interface TypeDeterminer {
  objects: FullObjTestBlock;
  name: string;
}
// For minification we pass in consts.
const FUNCTION = 'function';
const NUMBER = 'number';
const PROPERTY = 'property';
const STRING = 'string';

export const Types: Record<string, TypeDeterminer> = {
  SHAKA: {
    name: PlayerNames.SHAKA_PLAYER,
    objects: {
      addEventListener: FUNCTION,
      getAssetUri: FUNCTION,
      getLoadMode: FUNCTION,
      getStats: FUNCTION,
      load: FUNCTION,
      unload: FUNCTION
    }
  },
  HLS: {
    name: PlayerNames.HLS_PLAYER,
    objects: {
      attachMedia: FUNCTION,
      detachMedia: FUNCTION,
      loadSource: FUNCTION
    }
  },
  HLSWrapper: {
    name: 'HLSWrapper',
    objects: {
      element: (o?: unknown): ObjTestFuncResult => {
        return { res: o instanceof HTMLVideoElement, str: 'HTMLVideoElement' };
      },
      config: (o?: unknown): ObjTestFuncResult => {
        return { res: typeof o === 'object', str: 'hlsConfig' };
      },
      options: {
        version: STRING
      }
    }
  },
  BITMOVIN_SET_SOURCE: {
    name: PlayerNames.BITMOVIN_PLAYER,
    objects: {
      load: FUNCTION
    }
  },
  BITMOVIN_ATTACH: {
    name: PlayerNames.BITMOVIN_PLAYER,
    objects: {
      getCurrentTime: FUNCTION,
      getDuration: FUNCTION,
      getPlaybackSpeed: FUNCTION,
      getVideoElement: FUNCTION,
      getVideoQuality: FUNCTION,
      on: FUNCTION
    }
  },
  BitmovinOptions: {
    name: 'BitmovinOptions',
    objects: {
      container: (o?: unknown): ObjTestFuncResult => {
        return { res: o instanceof HTMLElement, str: 'HTMLElement' };
      },
      playerConfig: {
        key: STRING
      }
    }
  },
  FLOW: {
    name: PlayerNames.FLOW_PLAYER,
    objects: {
      setSrc: FUNCTION,
      opts: {
        hls: (o?: unknown): ObjTestFuncResult => {
          return { res: typeof o === 'object', str: 'hls' };
        }
      }
    }
  },
  VIDEOJS: {
    name: PlayerNames.VIDEOJS_PLAYER,
    objects: {
      src: FUNCTION,
      tech: FUNCTION
    }
  },
  SHAKA_WINDOW: {
    name: 'window',
    objects: {
      shaka: {
        net: {
          NetworkingEngine: {
            registerScheme: FUNCTION,
            PluginPriority: {
              APPLICATION: NUMBER
            }
          }
        },
        util: {
          Error: FUNCTION
        }
      }
    }
  },
  FETCH_FACTORY: {
    name: 'Fetch Factory Player',
    objects: {
      addEventListener: FUNCTION,
      setFetchFactory: FUNCTION
    }
  },
  FETCH_FACTORY_PLAYER_BITRATE_LISTENER: {
    name: 'FetchFactoryPlayer-setSource',
    objects: {
      bitratechangedEventName: STRING,
      getCurrentBitrate: FUNCTION
    }
  },
  FETCH_FACTORY_SET_SOURCE: {
    name: 'FetchFactoryPlayer-setSource',
    objects: {
      setSource: FUNCTION,
      getCurrentSource: FUNCTION
    }
  }
};

interface ObjTestFuncResult {
  res: boolean;
  str: string;
}

type ObjTestFunc = (o?: unknown) => ObjTestFuncResult;

export const compareObjectStructure = (
  name: string,
  item: unknown,
  comp: FullObjTestBlock,
  missing: Record<string, string[]>,
  messages: string[],
  setting?: string
): void => {
  if (!item || !comp) {
    return;
  }
  Object.entries(comp).forEach(([key, expected]) => {
    const prop = setting ? [setting, key].join('.') : key;
    const obj = item as Record<string, unknown>;
    const current = obj[key];
    if (!current) {
      const k = isString(expected) ? expected : PROPERTY;
      if (!missing[k]) {
        missing[k] = [];
      }
      missing[k].push(prop);
    } else if (isString(expected)) {
      const received = typeof current;
      if (received !== expected) {
        const params = { expected, name, prop, received };
        messages.push(detemplate(INCORRECT_TYPE, params));
      }
    } else if (typeof expected === 'function') {
      const func = expected as ObjTestFunc;
      const { res, str } = func(current);
      if (!res) {
        const received = typeof current;
        const params = { expected: str, name, prop, received };
        messages.push(detemplate(INCORRECT_TYPE, params));
      }
    } else {
      compareObjectStructure(name, current, expected, missing, messages, prop);
    }
  });
};

export interface TypeCheck {
  isType: boolean;
  data?: KsdkEventData;

  // Emits an error if the item is not of correct type
  emitErrorIfNotCorrectType: () => void;

  // logs a message if the item is not of correct type
  log: EmitLogFunction;
}

type EmitLogFunction = (log: Logger, level: Level, msg?: string) => void;
const emitLog = (log: Logger, level: Level, ...msg: LogMessage[]): void => {
  const logLevel = LogLevels[level];
  log.message(logLevel, ...msg);
};

export const logUnableToSetupMessage = (
  name: string,
  log: Logger,
  checker: TypeCheck
): void => {
  const { UNABLE_TO_SETUP_INTEGRATION } = MessageTemplates;
  const message = detemplate(UNABLE_TO_SETUP_INTEGRATION, { name });
  checker.log(log, log.ERROR, message);
};

/**
 * @param item - object to check
 * @param typeDeterminer - pattern to check against
 * @param quick - if true skip gathering the data object and just return noop
 *        functions, and whether or not the item matches
 *
 * @returns object of type TypeCheck
 */
export const checkType = (
  item: unknown,
  typeDeterminer: TypeDeterminer,
  quick?: boolean
): TypeCheck => {
  const { name, objects } = typeDeterminer;
  const missing: Record<string, string[]> = {},
    messages: string[] = [];
  // HLS missing required function(s): a, b, c
  // HLS has incorrect type for a expected a function received a string
  compareObjectStructure(name, item, objects, missing, messages);
  const keys: string[] = Object.keys(missing);
  const log = noop as EmitLogFunction;
  if (keys.length == 0 && messages.length == 0) {
    return { isType: true, emitErrorIfNotCorrectType: noop, log };
  }
  if (quick) {
    return { isType: false, emitErrorIfNotCorrectType: noop, log };
  }
  const reason = detemplate(PASSED_INVALID, { name });
  keys.sort();
  keys.forEach(k => {
    const f = missing[k];
    f.sort();
    const fields = f.join(', ');
    const s = f.length > 1 ? 's' : '';
    const params = { name, s, type: k, fields };
    messages.push(detemplate(MISSING_FIELDS, params));
  });
  const data = { reason, messages };
  const emitErrorIfNotCorrectType = () => {
    dispatchError(data);
  };
  const logfunc = (log: Logger, level: Level, msg?: string) => {
    const msgs = msg ? [msg, data] : [data];
    emitLog(log, level, msgs);
  };
  return { isType: false, data, emitErrorIfNotCorrectType, log: logfunc };
};

export const isHls = (obj?: unknown): obj is HLS => {
  return checkType(obj, Types.HLS, true).isType;
};

export const isShakaPlayer = (obj?: unknown): obj is ShakaPlayer => {
  return checkType(obj, Types.SHAKA, true).isType;
};
